/**
 * O Desk Cloud (docs/spec/desk-cloud.md): a lateral (as visões, as mesas, as pastas e o espaço), a
 * lista do lugar aberto e os detalhes do arquivo, como no esboço. Os menus do botão direito valem em
 * todo lugar (também no ⋯ e ao segurar o dedo). No celular, a lateral vira o seletor de lugar e os
 * detalhes, uma folha.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { useT } from '../i18n/index.js';
import type { DeskFile, DeskFolder } from '../shared.js';
import { useLoggedNav } from '../states/nav.js';
import { useApp } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { useMobile } from '../ui/mobile.js';
import { formatBytes } from '../bytes.js';
import {
  copyLink,
  deleteFolder,
  downloadFiles,
  downloadZip,
  enqueueUploads,
  favoriteFiles,
  moveFiles,
  moveFolder,
  newFolder,
  purgeFiles,
  renameFile,
  renameFolder,
  restoreFiles,
  setKind,
  trashFiles,
} from './cloudActions.js';
import { fileMenu, folderMenu, selectionMenu, trashMenu, type CloudActions, type FolderActions, type MenuContext } from './cloudMenus.js';
import { useCloud } from './cloudStore.js';
import { CloudUiContext, type CloudUi, type UploadTarget } from './cloudUi.js';
import { CloudBrowser } from './CloudBrowser.js';
import { CloudDetails } from './CloudDetails.js';
import { FolderPickerDialog, ShareDialog } from './CloudDialogs.js';
import { CloudPreview } from './CloudPreview.js';
import { CloudSidebar } from './CloudSidebar.js';
import { applyInRoom } from './inRoom.js';
import { backfillThumbs } from './thumbs.js';
import { UploadTray } from './UploadTray.js';
import './cloud.css';

export function DeskCloudState() {
  const t = useT();
  const mobile = useMobile();
  const me = useApp((s) => s.user?.id ?? null);
  const token = useApp((s) => s.token);
  const filesVersion = useApp((s) => s.filesVersion);
  const plan = useApp((s) => s.plan);
  const rooms = useApp((s) => s.rooms);
  const sub = useLoggedNav((s) => s.sub);
  const summary = useCloud((s) => s.summary);
  const page = useCloud((s) => s.page);
  const at = useCloud((s) => s.at);
  const focus = useCloud((s) => s.focus);
  const recentFolders = useCloud((s) => s.recentFolders);
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; entries: MenuEntry[]; label: string } | null>(null);
  const [share, setShare] = useState<DeskFile[] | null>(null);
  const [picker, setPicker] = useState<{ files: DeskFile[] } | { folder: DeskFolder } | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pickTarget = useRef<UploadTarget>({});

  // relê ao abrir e a cada aviso do servidor (outra aba, outro aparelho, um envio daqui)
  useEffect(() => {
    void useCloud.getState().load();
  }, [filesVersion, token]);

  // vindo de outro lugar (o painel da mesa: "Mostrar no Desk Cloud"), abre no lugar e no arquivo
  const deepAt = sub.kind === 'cloud' ? sub.at : undefined;
  const deepFocus = sub.kind === 'cloud' ? sub.focus : undefined;
  useEffect(() => {
    if (deepAt) useCloud.getState().go(deepAt);
    if (deepFocus) useCloud.getState().setFocus(deepFocus);
  }, [deepAt, deepFocus]);

  // as imagens de antes, sem miniatura: o dono gera as que faltam
  useEffect(() => {
    if (page && me) void backfillThumbs(page.files, me);
  }, [page, me]);

  const folders = useMemo(() => summary?.folders ?? [], [summary]);
  const menuContext: MenuContext = useMemo(
    () => ({ t, rooms: summary?.rooms ?? [], recentFolders: recentFolders.map((id) => folders.find((folder) => folder.id === id)).filter((folder): folder is DeskFolder => !!folder) }),
    [t, summary, recentFolders, folders],
  );

  const openFile = useCallback(
    (file: DeskFile) => {
      setPreview(file.id);
      useCloud.getState().setFocus(file.id);
      if (token && file.ownerId === me) void api.fileUsed(token, file.id).catch(() => undefined);
    },
    [token, me],
  );

  const uploadTarget = useCallback((): UploadTarget => {
    if (at.view === 'room') return { roomId: at.roomId };
    if (at.view === 'folder') return { folderId: at.folderId };
    return {};
  }, [at]);

  const upload = useCallback(
    (target?: UploadTarget) => {
      pickTarget.current = target ?? uploadTarget();
      inputRef.current?.click();
    },
    [uploadTarget],
  );

  const actions: CloudActions = useMemo(
    () => ({
      open: openFile,
      use: (file, action, roomId) => void applyInRoom(file, action, roomId),
      download: (files) => void downloadFiles(files),
      copyLink: (file) => void copyLink(file),
      rename: (file) => void renameFile(file),
      moveTo: (files, folderId) => void moveFiles(files, folderId),
      pickFolder: (files) => setPicker({ files }),
      setKind: (files, kind) => void setKind(files, kind),
      favorite: (files, on) => void favoriteFiles(files, on),
      share: (files) => setShare(files),
      trash: (files) => void trashFiles(files),
      restore: (files) => void restoreFiles(files),
      purge: (files) => void purgeFiles(files),
    }),
    [openFile],
  );

  const folderActions: FolderActions = useMemo(
    () => ({
      openFolder: (folder) => useCloud.getState().go({ view: 'folder', folderId: folder.id }),
      newFolder: (parentId) => void newFolder(parentId),
      uploadToFolder: (folder) => upload({ folderId: folder.id }),
      renameFolder: (folder) => void renameFolder(folder),
      moveFolder: (folder) => setPicker({ folder }),
      zipFolder: (folder) => void downloadZip({ folderId: folder.id }),
      deleteFolder: (folder) => void deleteFolder(folder),
    }),
    [upload],
  );

  const openMenu = useCallback((anchor: MenuAnchor, entries: MenuEntry[], label: string) => setMenu({ anchor, entries, label }), []);

  const menuForFile = useCallback(
    (file: DeskFile, anchor: MenuAnchor) => {
      const state = useCloud.getState();
      // clicar com o direito fora da seleção troca a seleção por ele, como nos gerenciadores de arquivos
      const inSelection = state.selected.has(file.id) && state.selected.size > 1;
      const files = inSelection ? state.filesOf(state.selected) : [file];
      if (!inSelection && state.selected.size && !state.selected.has(file.id)) state.clearSelection();
      const entries = file.trashedAt !== null ? trashMenu(menuContext, files, actions) : inSelection ? selectionMenu(menuContext, files, actions) : fileMenu(menuContext, file, actions);
      setMenu({ anchor, entries, label: inSelection ? t('cloud.selected', { n: files.length }) : file.name });
    },
    [menuContext, actions, t],
  );

  const menuForFolder = useCallback((folder: DeskFolder, anchor: MenuAnchor) => setMenu({ anchor, entries: folderMenu(menuContext, folder, folderActions), label: folder.name }), [menuContext, folderActions]);

  const roomName = useCallback(
    (roomId: string) => summary?.rooms.find((room) => room.id === roomId)?.name ?? rooms.find((room) => room.id === roomId)?.name ?? t('cloud.details.roomLabel'),
    [summary, rooms, t],
  );

  const ui: CloudUi = useMemo(
    () => ({ me, mobile, menuContext, actions, folderActions, openMenu, menuForFile, menuForFolder, openFile, upload, uploadTarget, roomName }),
    [me, mobile, menuContext, actions, folderActions, openMenu, menuForFile, menuForFolder, openFile, upload, uploadTarget, roomName],
  );

  const focused = page?.files.find((file) => file.id === focus) ?? null;
  const dropWhere = at.view === 'room' ? `«${roomName(at.roomId)}»` : at.view === 'folder' ? `«${folders.find((folder) => folder.id === at.folderId)?.name ?? ''}»` : t('cloud.drop.cloud');
  const canDrop = at.view !== 'trash';

  return (
    <CloudUiContext.Provider value={ui}>
      <div
        className={`rd-cloud${focused && !mobile ? ' has-details' : ''}`}
        onDragEnter={(e) => {
          if (canDrop && e.dataTransfer.types.includes('Files')) setDragging(true);
        }}
        onDragOver={(e) => {
          if (canDrop && e.dataTransfer.types.includes('Files')) e.preventDefault();
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragging(false);
          if (canDrop && e.dataTransfer.files.length) enqueueUploads(e.dataTransfer.files, uploadTarget());
        }}
      >
        {mobile ? null : <CloudSidebar />}
        <CloudBrowser />
        {focused ? <CloudDetails key={`${focused.id}:${focused.name}`} file={focused} onClose={() => useCloud.getState().setFocus(null)} /> : null}
        <UploadTray />
        {dragging ? (
          <div className="rd-cloud__drop" aria-hidden="true">
            <strong>{t('cloud.drop', { where: dropWhere })}</strong>
            <span>{t('cloud.drop.hint', { size: plan ? formatBytes(plan.entitlements.maxFileBytes) : '—' })}</span>
          </div>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            const list = e.target.files;
            if (list?.length) enqueueUploads(list, pickTarget.current);
            e.target.value = '';
          }}
        />
        {menu ? <ContextMenu anchor={menu.anchor} entries={menu.entries} label={menu.label} onClose={() => setMenu(null)} /> : null}
        {share ? <ShareDialog files={share} onClose={() => setShare(null)} /> : null}
        {picker ? (
          <FolderPickerDialog
            folders={folders}
            moving={'folder' in picker ? picker.folder : null}
            title={'folder' in picker ? t('cloud.folder.pickFolder', { name: picker.folder.name }) : t('cloud.folder.pick')}
            onPick={(folderId) => {
              if ('folder' in picker) void moveFolder(picker.folder, folderId);
              else void moveFiles(picker.files, folderId);
              setPicker(null);
            }}
            onClose={() => setPicker(null)}
          />
        ) : null}
        {preview && page ? <CloudPreview files={page.files} fileId={preview} onPick={(id) => setPreview(id)} onClose={() => setPreview(null)} /> : null}
      </div>
    </CloudUiContext.Provider>
  );
}
